import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Home, Plus, Edit, Trash2, Eye, MapPin, BarChart3, MessageSquare, Building2, User, Loader2, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Header from "../components/Header";
import ListingForm from "../components/ListingForm";
import ChatList from "../components/ChatList";
import FavoritesList from "../components/FavoritesList";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [stats, setStats] = useState(null);
  const [viewingRequests, setViewingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [listingsRes, statsRes, viewingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/my-listings`),
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/viewing-requests`)
      ]);
      setListings(listingsRes.data);
      setStats(statsRes.data);
      setViewingRequests(viewingsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListing = async (formData) => {
    setSaving(true);
    try {
      await axios.post(`${API}/dashboard/listings`, formData);
      
      toast.success("Anunț creat cu succes!");
      setShowAddDialog(false);
      fetchDashboardData();
    } catch (error) {
      console.error("Error creating listing:", error);
      toast.error(error.response?.data?.detail || "Eroare la crearea anunțului");
    } finally {
      setSaving(false);
    }
  };

  const handleEditListing = async (formData) => {
    if (!editingListing) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/dashboard/listings/${editingListing.id}`, formData);
      
      toast.success("Anunț actualizat cu succes!");
      setShowEditDialog(false);
      setEditingListing(null);
      fetchDashboardData();
    } catch (error) {
      console.error("Error updating listing:", error);
      toast.error(error.response?.data?.detail || "Eroare la actualizarea anunțului");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (listing) => {
    setEditingListing(listing);
    setShowEditDialog(true);
  };

  const handleDelete = async (listingId) => {
    try {
      await axios.delete(`${API}/dashboard/listings/${listingId}`);
      setListings(listings.filter(l => l.id !== listingId));
      toast.success("Anunț șters cu succes");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la ștergerea anunțului");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      detected: "bg-gray-100 text-gray-700",
      invited: "bg-blue-100 text-blue-700",
      claimed: "bg-amber-100 text-amber-700",
      published: "bg-emerald-100 text-emerald-700"
    };
    const labels = {
      detected: "Detectat",
      invited: "Invitat",
      claimed: "Revendicat",
      published: "Publicat"
    };
    return (
      <Badge className={styles[status] || "bg-gray-100"}>
        {labels[status] || status}
      </Badge>
    );
  };

  const isAgent = user?.user_type === 'agent';

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">
                Dashboard
              </h1>
              {isAgent ? (
                <Badge className="bg-amber-500 text-white">
                  <Building2 className="w-3 h-3 mr-1" />
                  Agent
                </Badge>
              ) : (
                <Badge variant="outline">
                  <User className="w-3 h-3 mr-1" />
                  Proprietar
                </Badge>
              )}
            </div>
            <p className="text-stone-500 mt-1">
              {user?.name || user?.email}
            </p>
          </div>
          
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
            disabled={!isAgent && stats?.listings?.remaining === 0}
            data-testid="add-listing-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Adaugă anunț
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Home className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.listings.total}</p>
                  <p className="text-sm text-stone-500">Anunțuri</p>
                </div>
              </div>
              {!isAgent && (
                <p className="text-xs text-stone-400 mt-2">
                  {stats.listings.remaining} din {stats.listings.max_allowed} disponibile
                </p>
              )}
            </div>
            
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.views}</p>
                  <p className="text-sm text-stone-500">Vizualizări</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.viewing_requests}</p>
                  <p className="text-sm text-stone-500">Cereri vizionare</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.listings.published}</p>
                  <p className="text-sm text-stone-500">Publicate</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl">
            <TabsTrigger value="listings" className="rounded-lg" data-testid="listings-tab">
              Anunțurile mele ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="favorites" className="rounded-lg" data-testid="favorites-tab">
              <Heart className="w-4 h-4 mr-1" />
              Favorite
            </TabsTrigger>
            <TabsTrigger value="requests" className="rounded-lg" data-testid="requests-tab">
              Cereri vizionare ({viewingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-lg" data-testid="messages-tab">
              <MessageSquare className="w-4 h-4 mr-1" />
              Mesaje
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : listings.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl card-shadow" data-testid="no-listings">
                <Home className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-700 mb-2">Nu ai încă anunțuri</h3>
                <p className="text-stone-500 mb-6">
                  Creează primul tău anunț pentru a-l afișa pe platformă
                </p>
                <Button 
                  onClick={() => setShowAddDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adaugă primul anunț
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="listings-grid">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className="bg-white rounded-xl p-4 sm:p-6 card-shadow flex flex-col sm:flex-row items-start gap-4"
                    data-testid={`listing-${listing.id}`}
                  >
                    <div className="w-full sm:w-32 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-stone-100">
                      <img
                        src={listing.images?.[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=200"}
                        alt={listing.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(listing.status)}
                          </div>
                          <h3 className="font-semibold text-stone-900">
                            {listing.title}
                          </h3>
                          <p className="text-stone-500 text-sm flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {listing.zone || listing.city}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-700">
                            {listing.price?.toLocaleString('ro-RO')} {listing.currency || '€'}
                          </p>
                          <p className="text-stone-400 text-sm">
                            {listing.rooms} cam • {listing.surface} mp
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
                      <Link to={`/proprietate/${listing.id}`} className="flex-1 sm:flex-none">
                        <Button variant="outline" size="sm" className="w-full rounded-lg">
                          <Eye className="w-4 h-4 mr-1" />
                          Vezi
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 sm:flex-none rounded-lg"
                        onClick={() => openEditDialog(listing)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editează
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1 sm:flex-none rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Ștergi anunțul?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Această acțiune nu poate fi anulată. Anunțul va fi șters permanent.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Anulează</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(listing.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Șterge
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            <div className="bg-white rounded-2xl card-shadow p-6">
              <FavoritesList />
            </div>
          </TabsContent>

          {/* Viewing Requests Tab */}
          <TabsContent value="requests">
            {viewingRequests.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl card-shadow">
                <MessageSquare className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-stone-700 mb-2">Nu ai cereri de vizionare</h3>
                <p className="text-stone-500">
                  Când cineva va solicita o vizionare, vei vedea cererea aici
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {viewingRequests.map((request, index) => (
                  <div key={index} className="bg-white rounded-xl p-4 card-shadow">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-stone-900">{request.name}</h4>
                        <p className="text-sm text-stone-500">{request.listing_title}</p>
                      </div>
                      <span className="text-xs text-stone-400">
                        {new Date(request.created_at).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <a href={`tel:${request.phone}`} className="text-blue-600 hover:underline text-sm">
                        {request.phone}
                      </a>
                      {request.email && (
                        <a href={`mailto:${request.email}`} className="text-blue-600 hover:underline text-sm">
                          {request.email}
                        </a>
                      )}
                    </div>
                    {request.message && (
                      <p className="mt-3 text-sm text-stone-600 bg-stone-50 p-3 rounded-lg">
                        {request.message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="bg-white rounded-2xl card-shadow p-6">
              <ChatList />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Listing Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Adaugă anunț nou</DialogTitle>
          </DialogHeader>
          <ListingForm
            onSave={handleCreateListing}
            onCancel={() => setShowAddDialog(false)}
            saving={saving}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Listing Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open);
        if (!open) setEditingListing(null);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Editează anunț</DialogTitle>
          </DialogHeader>
          {editingListing && (
            <ListingForm
              listing={editingListing}
              onSave={handleEditListing}
              onCancel={() => {
                setShowEditDialog(false);
                setEditingListing(null);
              }}
              saving={saving}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwnerDashboard;
